import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  GivingPerson,
  Organization,
  PaymentMethod,
  Statement,
  Text2GivePhoneNumber,
  RecurringDonationsAPIResponse,
  RecurringDonationAPIResponse,
  RecurringDonationDesignationAPIResponse,
  RecurringDonation,
  Designation,
  FundAPIResponse,
  PledgesAPIResponse,
  PledgeAPIResponse,
  PledgeCampaignAPIResponse,
  Pledge,
  PledgeCampaign,
  isDesignation,
  isFund,
  isPledgeCampaign,
  isGivingPerson,
} from "./types"
import { Fund } from "source/giving/donation_form/types"

export async function fetchGivingPerson(): Promise<GivingPerson> {
  const response = await sessionApiClient.get("/giving/v2/me")
  return response.data.attributes
}

export async function fetchText2GivePhoneNumber(): Promise<Text2GivePhoneNumber | null> {
  const response = await sessionApiClient.get(
    "/giving/v2/me/text_to_give_phone_number",
  )
  if (response.data) {
    const { attributes, relationships } = response.data
    return {
      id: response.data.id,
      phone_number: attributes.phone_number,
      payment_method_id: relationships?.payment_method?.data?.id ?? null,
    }
  } else {
    return null
  }
}

export async function fetchStatements(): Promise<Statement[]> {
  const response = await sessionApiClient.get("/giving/v2/me/statements")
  return response.data.map(
    (item: { id: string; attributes: Omit<Statement, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchOrganization(): Promise<Organization> {
  const response = await sessionApiClient.get("/giving/v2")
  return {
    id: response.data.id,
    ...response.data.attributes,
  }
}

export async function fetchFunds(): Promise<Fund[]> {
  const response = await sessionApiClient.get("/giving/v2/funds")
  return response.data.map(
    (item: { id: string; attributes: Omit<Fund, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await sessionApiClient.get("/giving/v2/me/payment_methods")
  return response.data.map(
    (item: { id: string; attributes: Omit<PaymentMethod, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchRecurringDonations(): Promise<{
  recurringDonations: RecurringDonation[]
}> {
  const response: RecurringDonationsAPIResponse = await sessionApiClient.get(
    "/giving/v2/me/recurring_donations?include=designations,designations.fund",
  )
  return parseRecurringDonationsResponse(response)
}

function parseRecurringDonationsResponse(
  response: RecurringDonationsAPIResponse,
) {
  const designationFunds: Fund[] = response.included
    .filter(isFund)
    .map((item: FundAPIResponse) => ({
      id: item.id,
      ...item.attributes,
    }))

  const designations: Designation[] = response.included
    .filter(isDesignation)
    .map((item: RecurringDonationDesignationAPIResponse) => ({
      id: item.id,
      ...item.attributes,
      fund: designationFunds.find(
        (fund) => fund.id === item.relationships.fund.data.id,
      )!,
    }))

  const recurringDonations: RecurringDonation[] = response.data.map(
    (item: RecurringDonationAPIResponse) => ({
      id: item.id,
      ...item.attributes,
      payment_method_id: item.relationships.payment_method.data?.id || null,
      designations: item.relationships.designations.data.map((designation) => {
        const includedDesignation: Designation = designations.find(
          (d) => d.id === designation.id,
        )!
        return {
          ...includedDesignation,
        }
      }),
    }),
  )

  return { recurringDonations }
}

export async function fetchPledges(): Promise<{
  pledges: Pledge[]
  pledgeCampaigns: PledgeCampaign[]
  jointGiver: GivingPerson | null
}> {
  const response: PledgesAPIResponse = await sessionApiClient.get(
    "/giving/v2/me/pledges?include=joint_giver,pledge_campaign.fund",
  )

  return parsePledgesResponse(response)
}

function parsePledgesResponse(response: PledgesAPIResponse) {
  const jointGiver: GivingPerson | null =
    response.included.filter(isGivingPerson)[0]?.attributes || null

  const pledgeFunds: Fund[] = response.included
    .filter(isFund)
    .map((item: FundAPIResponse) => ({
      id: item.id,
      ...item.attributes,
    }))

  const pledgeCampaigns: PledgeCampaign[] = response.included
    .filter(isPledgeCampaign)
    .map((item: PledgeCampaignAPIResponse) => ({
      id: item.id,
      ...item.attributes,
      fund: pledgeFunds.find(
        (fund) => fund.id === item.relationships.fund.data.id,
      )!,
    }))

  const pledges: Pledge[] = response.data.map((item: PledgeAPIResponse) => ({
    id: item.id,
    ...item.attributes,
    joint_giver: jointGiver || null,
    pledge_campaign: pledgeCampaigns.find(
      (campaign) => campaign.id === item.relationships.pledge_campaign.data.id,
    )!,
    fund: pledgeFunds.find(
      (fund) => fund.id === item.relationships.fund.data.id,
    )!,
  }))

  return { pledges, pledgeCampaigns, jointGiver }
}
